"use client";

import React from "react";
import { Activity, Coins, Database, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const utilityDrivers = [{
  icon: Activity,
  title: "Token Distribution",
  description: "The Aizalea token supply is carefully allocated to ensure sustainable ecosystem growth, strategic partnerships, community incentives and long term development. The distribution balances infrastructure expansion, liquidity provisioning and ecosystem participation across the AizaHcare network."
}, {
  icon: Database,
  title: "Ecosystem Incentives",
  description: "A significant portion of the token supply is dedicated to ecosystem growth. These incentives reward contributors, partners and early supporters who help expand the AizaHcare ecosystem and accelerate the adoption of AI driven healthcare solutions."
}, {
  icon: Coins,
  title: "Liquidity & Market Stability",
  description: "Part of the token allocation is reserved for liquidity provisioning and market stability. This allocation supports healthy trading conditions, reduces volatility and ensures a sustainable token economy as the AizaHcare ecosystem continues to expand."
}];
export default function UnderstandingAizaleaTokenomics() {
  const {
    ref: leftRef,
    isVisible: isLeftVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: isRightVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="understandingaizaleatokenomics" className="bg-primary text-primary-foreground py-24 lg:py-32 overflow-hidden relative">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] opacity-20 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-b from-primary-foreground/10 to-transparent blur-3xl rounded-full" />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center justify-items-center lg:justify-items-start text-center lg:text-left">
          
          {}
          <div ref={leftRef} className="transition-all duration-1000 ease-out opacity-100 translate-y-0 !w-auto">
            <Badge variant="secondary" className="mb-6 bg-primary-foreground/10 text-primary-foreground hover:bg-primary-foreground/20 border-transparent">
              Tokenomics & Utility
            </Badge>
            
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 tracking-tight font-sans">
              The Engine of <br className="hidden md:block" />
              <span className="text-primary-foreground/70">Clinical Intelligence</span>
            </h2>
            
            <p className="text-lg md:text-xl text-primary-foreground/80 mb-10 leading-relaxed font-sans max-w-xl">
              AIZALEA is not just a token; it is the foundational utility layer powering the AizaHcare ecosystem. It bridges real-world medical infrastructure with advanced AI compute.
            </p>

            {}
            <div className="mb-12 p-8 bg-primary-foreground/5 rounded-3xl border border-primary-foreground/10 backdrop-blur-sm w-full max-w-2xl mx-auto lg:mx-0">
              <div className="text-sm text-primary-foreground/60 uppercase tracking-widest mb-3 font-semibold">
                Total Fixed Supply
              </div>
              <div className="text-5xl font-mono font-bold text-primary-foreground tracking-tighter !rounded-none md:text-6xl">
                2,000,000,000
                <span className="text-2xl md:text-3xl text-primary-foreground/40 ml-3 font-sans tracking-normal"></span>
              </div>
            </div>

            {}
           <div className="space-y-8 mb-12 px-6 md:px-0">
  {utilityDrivers.map((item, i) => <div key={i} data-index={i} className="flex items-start gap-3 md:gap-5 text-left">
      <div className="mt-1 shrink-0">
        <div className="flex items-center justify-center w-8 h-8 md:w-12 md:h-12 rounded-xl md:rounded-2xl bg-primary-foreground/10 border border-primary-foreground/20">
          <item.icon className="w-4 h-4 md:w-6 md:h-6 text-primary-foreground" />
        </div>
      </div>

      <div className="min-w-0 flex-1 pr-1">
        <h3 className="text-lg md:text-xl font-semibold mb-2 text-primary-foreground leading-tight break-words">
          {item.title}
        </h3>

        <p className="text-sm md:text-base text-primary-foreground/70 leading-relaxed break-words">
          {item.description}
        </p>
      </div>
    </div>)}
          </div>

            <div className="flex flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 h-14 px-8 text-base rounded-xl">
                
              </Button>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`transition-all duration-1000 delay-200 ease-out ${isRightVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}>
            <div className="relative w-full max-w-2xl mx-auto aspect-square lg:aspect-[4/5] rounded-[2.5rem] overflow-hidden border border-primary-foreground/10 bg-primary-foreground/5 p-8 md:p-12 flex items-center justify-center group">
              {}
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,var(--color-primary-foreground)_0%,transparent_70%)] opacity-[0.03] !m-0 !w-auto" />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3/4 h-3/4 border border-primary-foreground/10 rounded-full border-dashed animate-[spin_60s_linear_infinite]" />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1/2 h-1/2 border border-primary-foreground/10 rounded-full border-dashed animate-[spin_40s_linear_infinite_reverse]" />
              
              {}
              <img src="https://positive-cattle.10web.cloud/wp-content/uploads/2026/03/B5EB6A55-FEBB-4B8A-957D-10612A799C31.png" alt="Aizalea Tokenomics Distribution Diagram" className="relative z-10 h-full max-h-[500px] object-contain drop-shadow-2xl transition-transform duration-700 group-hover:scale-105 !w-[400px] lg:max-h-full" />
              
              {}
              <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-transparent to-transparent pointer-events-none" />
            </div>
          </div>

        </div>
      </div>
    </section>;
}